import { Component, OnInit, Type } from '@angular/core';
import { IPluginPreviewComponent } from '../../../../interfaces/plugin-preview-component.interface';
import { IWizardPluginComponent } from '../../../../interfaces/wizard-plugin-component.interface';
import { Pair } from '../../../../models/pair';
import { BasePluginData } from '../../../../models/wizard-plugin-models/base-plugin-data';
import { SoftwareData } from '../../../../models/wizard-plugin-models/software-data';
import { ResourceMasterDataService } from '../../../../services/resource-master-data.service';
import { SoftwareFilterService } from '../../../../services/software-filter.service';
import { WizardPlugin } from '../../../../decorators/wizard-plugin';
import { SoftwarePreviewComponent } from './software-preview/software-preview.component';
import { Log, IShortLogger } from '../../../../services/logger.service';

interface SelectedPair extends Pair {
  selected?: boolean;
}

interface SelectedTypePair extends SelectedPair {
  type: string;
}

@WizardPlugin({
  name: "Software",
  optional: false,
  description: "Configure software settings like OS, SDKs, Frameworks, Databases & Development Tools",
  dataModelType: SoftwareData
})
@Component({
  selector: 'app-software-config-plugin',
  templateUrl: './software-config-plugin.component.html',
  styleUrl: './software-config-plugin.component.css'
})
export class SoftwareConfigPluginComponent implements IWizardPluginComponent, OnInit {
  private readonly logger: IShortLogger<SoftwareConfigPluginComponent> = Log.create(SoftwareConfigPluginComponent);
  softwareData: SoftwareData = new SoftwareData();

  // OS options
  osOptions: Pair[] = [];
  osVersionOptions: Pair[] = [];
  osVersionSearch = '';
  filteredOsVersions: Pair[] = [];

  filterOsVersions(query: string | any): void {
    if (typeof query !== 'string') return;
    const q = query.toLowerCase();
    this.filteredOsVersions = q
      ? this.osVersionOptions.filter(v => v.name.toLowerCase().includes(q))
      : this.osVersionOptions.slice();
  }

  onOsVersionSelected(v: Pair): void {
    this.softwareData.osVersion = v.id;
    this.osVersionSearch = v.name;
  }

  // SDK options with filtering
  sdkOptions: SelectedTypePair[] = [];
  filteredSdkOptions: SelectedTypePair[] = [];
  sdkTypes: string[] = ['all'];
  currentSdkFilter: string = 'all';

  // Database options with filtering
  databaseOptions: SelectedTypePair[] = [];
  filteredDatabaseOptions: SelectedTypePair[] = [];
  dbTypes: string[] = ['all'];
  currentDbFilter: string = 'all';

  // Editor options
  editorOptions: SelectedPair[] = [];

  miscOptions: SelectedPair[] = [];

  // Chip search inputs
  sdkSearchInput = '';
  dbSearchInput = '';
  editorSearchInput = '';
  miscSearchInput = '';

  // Chip autocomplete lists (options not yet selected, filtered by text)
  sdkAutocompleteOptions: SelectedTypePair[] = [];
  dbAutocompleteOptions: SelectedTypePair[] = [];
  editorAutocompleteOptions: SelectedPair[] = [];
  miscAutocompleteOptions: SelectedPair[] = [];

  getOptionName(options: { id: string; name: string }[], id: string): string {
    return options.find(o => o.id === id)?.name ?? id;
  }

  addChip(field: 'sdks' | 'databases' | 'editors' | 'misc', id: string): void {
    const arr = this.softwareData[field] as string[];
    if (!arr.includes(id)) {
      arr.push(id);
    }
    this.refreshAutocompleteOptions();
  }

  removeChip(field: 'sdks' | 'databases' | 'editors' | 'misc', id: string): void {
    const arr = this.softwareData[field] as string[];
    const idx = arr.indexOf(id);
    if (idx >= 0) arr.splice(idx, 1);
    this.refreshAutocompleteOptions();
  }

  onSdkSearch(q: string): void {
    const lower = (q || '').toLowerCase();
    this.sdkAutocompleteOptions = this.sdkOptions
      .filter(o => !this.softwareData.sdks.includes(o.id) && o.name.toLowerCase().includes(lower));
  }

  onDbSearch(q: string): void {
    const lower = (q || '').toLowerCase();
    this.dbAutocompleteOptions = this.databaseOptions
      .filter(o => !this.softwareData.databases.includes(o.id) && o.name.toLowerCase().includes(lower));
  }

  onEditorSearch(q: string): void {
    const lower = (q || '').toLowerCase();
    this.editorAutocompleteOptions = this.editorOptions
      .filter(o => !this.softwareData.editors.includes(o.id) && o.name.toLowerCase().includes(lower));
  }

  onMiscSearch(q: string): void {
    const lower = (q || '').toLowerCase();
    this.miscAutocompleteOptions = this.miscOptions
      .filter(o => !this.softwareData.misc.includes(o.id) && o.name.toLowerCase().includes(lower));
  }

  private refreshAutocompleteOptions(): void {
    this.sdkAutocompleteOptions = this.sdkOptions.filter(o => !this.softwareData.sdks.includes(o.id));
    this.dbAutocompleteOptions = this.databaseOptions.filter(o => !this.softwareData.databases.includes(o.id));
    this.editorAutocompleteOptions = this.editorOptions.filter(o => !this.softwareData.editors.includes(o.id));
    this.miscAutocompleteOptions = this.miscOptions.filter(o => !this.softwareData.misc.includes(o.id));
  }

  // Track initialization state
  private isInitialized = false;

  constructor(
    private masterDataService: ResourceMasterDataService,
    private softwareFilterService: SoftwareFilterService
  ) { }

  ngOnInit(): void {
    try {
      this.loadOsOptions();
      this.loadSdkOptions();
      this.loadDatabaseOptions();
      this.loadEditorOptions();
      this.loadMiscOptions();

      // Initialize with default values if needed
      this.onOsChangeSafe();
      this.updateSelectionModels();

      // Mark as initialized
      this.isInitialized = true;
      this.refreshAutocompleteOptions();

      this.selectedOperatingSystem = this.softwareData.os;
    } catch (error) {
      this.logger.e('Error initializing SoftwareConfigPluginComponent:', error);
    }
  }

  private loadOsOptions(): void {
    try {
      const osData = this.masterDataService?.get<Record<string, { name: string; isEnabled?: boolean }>>('software.os');
      if (osData) {
        this.osOptions = Object.entries(osData)
          .filter(([, group]) => group.isEnabled !== false)
          .map(([id, group]) => ({ id, name: group.name }));
      } else {
        this.osOptions = [{ id: 'windows', name: 'Windows' }];
      }

      // Set default OS if not already set or not in available options
      if (!this.osOptions.some(os => os.id === this.softwareData.os)) {
        this.softwareData.os = this.osOptions[0]?.id ?? 'windows';
      }
    } catch (error) {
      this.logger.e('Error loading OS options:', error);
      this.osOptions = [{ id: 'windows', name: 'Windows' }];
    }
  }

  private loadSdkOptions(): void {
    try {
      // Load SDK options from master data
      const sdkData = this.masterDataService?.get('software.sdks');
      if (sdkData && Array.isArray(sdkData)) {
        // Store raw options for later filtering
        const allSdkOptions = sdkData.map(sdk => ({
          ...sdk,
          selected: this.softwareData.sdks.includes(sdk.id)
        })) as any[];

        // Apply OS-based filtering only if both OS and version are set
        if (this.softwareData.os && this.softwareData.osVersion) {
          this.sdkOptions = this.softwareFilterService.filterByOs(
            allSdkOptions,
            this.softwareData.os,
            this.softwareData.osVersion
          ).map(sdk => ({
            ...sdk,
            selected: this.softwareData.sdks.includes(sdk.id)
          })) as any[];
        } else {
          // If OS or version not set, show all options
          this.sdkOptions = allSdkOptions;
        }

        // Extract unique SDK types for filtering
        const types = new Set<string>();
        types.add('all');
        this.sdkOptions.forEach(sdk => {
          if (sdk.type) types.add(sdk.type);
        });
        this.sdkTypes = Array.from(types);
      } else {
        // Fallback
        this.sdkOptions = [
          { id: 'dotnet8', name: '.NET 8', type: 'dotnet', selected: false },
          { id: 'node22', name: 'Node.js 22', type: 'node', selected: false }
        ];
        this.sdkTypes = ['all', 'dotnet', 'node'];
      }

      this.filteredSdkOptions = [...this.sdkOptions];
    } catch (error) {
      this.logger.e('Error loading SDK options:', error);
      this.sdkOptions = [
        { id: 'dotnet8', name: '.NET 8', type: 'dotnet', selected: false },
        { id: 'node22', name: 'Node.js 22', type: 'node', selected: false }
      ];
      this.filteredSdkOptions = [...this.sdkOptions];
    }
  }

  private loadDatabaseOptions(): void {
    try {
      // Load database options from master data
      const dbData = this.masterDataService?.get('software.databases');
      if (dbData && Array.isArray(dbData)) {
        // Store raw options for later filtering
        const allDbOptions = dbData.map(db => ({
          ...db,
          selected: this.softwareData.databases.includes(db.id)
        })) as any[];

        // Apply OS-based filtering only if both OS and version are set
        if (this.softwareData.os && this.softwareData.osVersion) {
          this.databaseOptions = this.softwareFilterService.filterByOs(
            allDbOptions,
            this.softwareData.os,
            this.softwareData.osVersion
          ).map(db => ({
            ...db,
            selected: this.softwareData.databases.includes(db.id)
          })) as any[];
        } else {
          // If OS or version not set, show all options
          this.databaseOptions = allDbOptions;
        }

        // Extract unique database types for filtering
        const types = new Set<string>();
        types.add('all');
        this.databaseOptions.forEach(db => {
          if (db.type) types.add(db.type);
        });
        this.dbTypes = Array.from(types);
      } else {
        // Fallback
        this.databaseOptions = [
          { id: 'mysql8', name: 'MySQL 8', type: 'sql', selected: false },
          { id: 'mongodb7', name: 'MongoDB 7', type: 'nosql', selected: false }
        ];
        this.dbTypes = ['all', 'sql', 'nosql'];
      }

      this.filteredDatabaseOptions = [...this.databaseOptions];
    } catch (error) {
      this.logger.e('Error loading database options:', error);
      this.databaseOptions = [
        { id: 'mysql8', name: 'MySQL 8', type: 'sql', selected: false },
        { id: 'mongodb7', name: 'MongoDB 7', type: 'nosql', selected: false }
      ];
      this.filteredDatabaseOptions = [...this.databaseOptions];
    }
  }

  private loadEditorOptions(): void {
    try {
      // Load editor options from master data
      const editorData = this.masterDataService?.get('software.editors');
      if (editorData && Array.isArray(editorData)) {
        // Store raw options for later filtering
        const allEditorOptions = editorData.map(editor => ({
          ...editor,
          selected: this.softwareData.editors.includes(editor.id)
        })) as any[];

        // Apply OS-based filtering only if both OS and version are set
        if (this.softwareData.os && this.softwareData.osVersion) {
          this.editorOptions = this.softwareFilterService.filterByOs(
            allEditorOptions,
            this.softwareData.os,
            this.softwareData.osVersion
          ).map(editor => ({
            ...editor,
            selected: this.softwareData.editors.includes(editor.id)
          })) as any[];
        } else {
          // If OS or version not set, show all options
          this.editorOptions = allEditorOptions;
        }
      }
    } catch (error) {
      this.logger.e('Error loading editor options:', error);
    }
  }

  private loadMiscOptions(): void {
    try {
      // Load misc options from master data
      const miscData = this.masterDataService?.get('software.misc');
      this.logger.i('miscData', miscData);
      if (miscData && Array.isArray(miscData)) {
        // Store raw options for later filtering
        const allMiscOptions = miscData.map(misc => ({
          ...misc,
          selected: this.softwareData.misc.includes(misc.id)
        })) as any[];

        // Apply OS-based filtering only if both OS and version are set
        if (this.softwareData.os && this.softwareData.osVersion) {
          this.miscOptions = this.softwareFilterService.filterByOs(
            allMiscOptions,
            this.softwareData.os,
            this.softwareData.osVersion
          ).map(misc => ({
            ...misc,
            selected: this.softwareData.misc.includes(misc.id)
          })) as any[];
        } else {
          // If OS or version not set, show all options
          this.miscOptions = allMiscOptions;
        }
      }
    } catch (error) {
      this.logger.e('Error loading misc options:', error);
    }
  }

  // Safe version of onOsChange that won't crash
  onOsChangeSafe(): void {
    try {
      if (!this.masterDataService) {
        this.logger.w('Master data service not available');
        this.osVersionOptions = [];
        return;
      }

      // Load OS versions based on selected OS
      const osKey = `software.os.${this.softwareData.os}`;
      const osData = this.masterDataService.get<{ name: string, versions: Pair[] }>(osKey);

      if (osData && Array.isArray(osData.versions)) {
        this.osVersionOptions = osData.versions;

        // Set default version if current is not in options
        if (!this.osVersionOptions.map(item => item.id).includes(this.softwareData.osVersion)) {
          this.softwareData.osVersion = this.osVersionOptions[0]?.id || '';
        }
        this.filteredOsVersions = this.osVersionOptions.slice();
        this.osVersionSearch = this.osVersionOptions.find(v => v.id === this.softwareData.osVersion)?.name ?? '';
      } else {
        // Try direct path
        const versions = this.masterDataService.get<Pair[]>(`${osKey}.versions`);

        if (versions && Array.isArray(versions)) {
          this.osVersionOptions = versions;

          // Set default version if current is not in options
          if (!this.osVersionOptions.map(item => item.id).includes(this.softwareData.osVersion)) {
            this.softwareData.osVersion = this.osVersionOptions[0]?.id || '';
          }
          this.filteredOsVersions = this.osVersionOptions.slice();
          this.osVersionSearch = this.osVersionOptions.find(v => v.id === this.softwareData.osVersion)?.name ?? '';
        } else {
          this.logger.w(`No versions found for OS: ${this.softwareData.os}`);
          this.osVersionOptions = [];
          this.filteredOsVersions = [];
          this.osVersionSearch = '';
          this.softwareData.osVersion = '';
        }
      }
    } catch (error) {
      this.logger.e('Error in onOsChangeSafe:', error);
      this.osVersionOptions = [];
      this.softwareData.osVersion = '';
    }
  }

  public selectedOperatingSystem: string = '';

  selectOperatingSystem(value: string): void {
    this.selectedOperatingSystem = value;
    this.softwareData.os = value; // Sync with your config model
    this.onOsChange();      // Call your existing handler
  }

  // UI event handler
  onOsChange(): void {
    this.onOsChangeSafe();
    // Reload and refilter all software options when OS changes
    this.loadSdkOptions();
    this.loadDatabaseOptions();
    this.loadEditorOptions();
    this.loadMiscOptions();
    this.refreshAutocompleteOptions();
  }

  updateSelectionModels(): void {
    try {
      // Update SDK selections
      this.sdkOptions.forEach(sdk => {
        sdk.selected = this.softwareData.sdks.includes(sdk.id);
      });

      // Update database selections
      this.databaseOptions.forEach(db => {
        db.selected = this.softwareData.databases.includes(db.id);
      });

      // Update editor selections
      this.editorOptions.forEach(editor => {
        editor.selected = this.softwareData.editors.includes(editor.id);
      });

      this.miscOptions.forEach(misc => {
        misc.selected = this.softwareData.misc.includes(misc.id);
      });

    } catch (error) {
      this.logger.e('Error updating selection models:', error);
    }
  }

  updateSdkSelections(e: any): void {
    try {
      this.softwareData.sdks = e.value;
    } catch (error) {
      this.logger.e('Error updating SDK selections:', error);
    }
  }

  updateDbSelections(e: any): void {
    try {
      this.softwareData.databases = e.value;
    } catch (error) {
      this.logger.e('Error updating database selections:', error);
    }
  }

  updateEditorSelections(e: any): void {
    try {
      this.softwareData.editors = e.value;
    } catch (error) {
      this.logger.e('Error updating editor selections:', error);
    }
  }

  updateMiscSelections($event: any) {
    try {
      this.softwareData.misc = $event.value;
    } catch (error) {
      this.logger.e('Error updating misc selections:', error);
    }
  }

  filterSdksByType(type: string): void {
    try {
      this.currentSdkFilter = type;
      if (type === 'all') {
        this.filteredSdkOptions = [...this.sdkOptions];
      } else {
        this.filteredSdkOptions = this.sdkOptions.filter(sdk => sdk.type === type);
      }
    } catch (error) {
      this.logger.e('Error filtering SDKs by type:', error);
      this.filteredSdkOptions = [...this.sdkOptions];
    }
  }

  filterDbsByType(type: string): void {
    try {
      this.currentDbFilter = type;
      if (type === 'all') {
        this.filteredDatabaseOptions = [...this.databaseOptions];
      } else {
        this.filteredDatabaseOptions = this.databaseOptions.filter(db => db.type === type);
      }
    } catch (error) {
      this.logger.e('Error filtering databases by type:', error);
      this.filteredDatabaseOptions = [...this.databaseOptions];
    }
  }

  getPluginData(): BasePluginData {
    return this.softwareData;
  }

  setPluginData(data: BasePluginData): void {
    try {
      if (data && 'os' in data) {
        this.softwareData = data as SoftwareData;

        // Only call onOsChange if we're initialized
        if (this.isInitialized) {
          this.onOsChangeSafe();
        }

        this.updateSelectionModels();
        this.refreshAutocompleteOptions();
      }
    } catch (error) {
      this.logger.e('Error setting plugin data:', error);
    }
  }

  validate(): boolean {
    return !!this.softwareData.os && !!this.softwareData.osVersion;
  }

  isComplete(): boolean {
    return this.validate();
  }

  reset(): void {
    try {
      this.softwareData = new SoftwareData();
      this.onOsChangeSafe();
      this.updateSelectionModels();
    } catch (error) {
      this.logger.e('Error resetting software config:', error);
    }
  }

  /**
   * Toggle the Hyper-V setting
   */
  toggleHyperV(): void {
    this.softwareData.hyperV = !this.softwareData.hyperV;
  }

  /**
   * Provide a preview component for the current configuration
   */
  asPreview(): Type<IPluginPreviewComponent> {
    return SoftwarePreviewComponent;
  }
}
